import homeResolutionArt from './home-resolution-art.json';
import constructionResolutionArt from './construction-resolution-art.json';

export const homeTextureVariants = {
  ...homeResolutionArt,
  ...constructionResolutionArt,
} as Readonly<Record<string, (typeof homeResolutionArt)[keyof typeof homeResolutionArt]>>;

/** Two samples per backing pixel, not per device pixel or CSS pixel. */
export function needsNativeHomeTexture(
  variantWidth: number,
  worldWidth: number,
  zoom: number,
  backingRatio: number,
): boolean {
  const values = [variantWidth, worldWidth, zoom, backingRatio];
  if (values.some((value) => !Number.isFinite(value) || value <= 0)) return true;
  return worldWidth * zoom * backingRatio * 2 > variantWidth;
}

export const nativeHomeTextureKey = (id: string) => `${id}__native`;

/** Independent of Phaser's required-art loader: sharpening must never lock gameplay. */
export class HomeTextureUpgradeQueue<T> {
  private readonly controller = new AbortController();
  private readonly pending = new Map<string, string>();
  private readonly active = new Set<string>();
  private readonly completed = new Set<string>();
  private readonly retryAfter = new Map<string, number>();

  constructor(
    private readonly load: (url: string, signal: AbortSignal) => Promise<T>,
    private readonly accept: (key: string, result: T) => void,
    private readonly now: () => number = Date.now,
  ) {}

  request(key: string, url: string) {
    if (
      this.controller.signal.aborted ||
      this.active.has(key) ||
      this.completed.has(key) ||
      (this.retryAfter.get(key) ?? 0) > this.now()
    )
      return;
    this.pending.set(key, url);
    this.pump();
  }

  dispose() {
    this.controller.abort();
    this.pending.clear();
    this.completed.clear();
    this.retryAfter.clear();
  }

  private pump() {
    while (!this.controller.signal.aborted && this.active.size < 2 && this.pending.size) {
      const [key, url] = this.pending.entries().next().value!;
      this.pending.delete(key);
      this.active.add(key);
      void this.load(url, this.controller.signal)
        .then((result) => {
          if (this.controller.signal.aborted) return;
          this.accept(key, result);
          this.completed.add(key);
          this.retryAfter.delete(key);
        })
        .catch(() => {
          // Keep the already drawn image. A subsequent camera/resize action can retry;
          // repeated touch frames cannot hammer a missing asset or take over the loader.
          if (!this.controller.signal.aborted) this.retryAfter.set(key, this.now() + 30_000);
        })
        .finally(() => {
          this.active.delete(key);
          this.pump();
        });
    }
  }
}

export function loadHomeTextureImage(url: string, signal: AbortSignal): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('Home texture request cancelled'));
      return;
    }
    const image = new Image();
    const cleanup = () => {
      clearTimeout(timeout);
      signal.removeEventListener('abort', cancel);
      image.onload = null;
      image.onerror = null;
    };
    const cancel = () => {
      cleanup();
      image.removeAttribute('src');
      reject(new Error('Home texture request cancelled or timed out'));
    };
    const timeout = setTimeout(cancel, 20_000);
    signal.addEventListener('abort', cancel, { once: true });
    image.onload = () => {
      cleanup();
      resolve(image);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('Home texture unavailable'));
    };
    image.src = url;
  });
}
